// ** Checks if an object is empty (returns boolean)
export const isObjEmpty = (obj: any) => Object.keys(obj).length === 0;

export const areEqual = (x: any, y: any) => {
  let leftChain: any[] = [],
    rightChain: any[] = [];

  let p;

  // remember that NaN === NaN returns false
  // and isNaN(undefined) returns true
  if (isNaN(x) && isNaN(y) && typeof x === "number" && typeof y === "number") {
    return true;
  }

  // Compare primitives and functions.
  // Check if both arguments link to the same object.
  // Especially useful on the step where we compare prototypes
  if (x === y) {
    return true;
  }

  // Works in case when functions are created in constructor.
  // Comparing dates is a common scenario. Another built-ins?
  // We can even handle functions passed across iframes
  if (
    (typeof x === "function" && typeof y === "function") ||
    (x instanceof Date && y instanceof Date) ||
    (x instanceof RegExp && y instanceof RegExp) ||
    (x instanceof String && y instanceof String) ||
    (x instanceof Number && y instanceof Number)
  ) {
    return x.toString() === y.toString();
  }

  // At last checking prototypes as good as we can
  if (!(x instanceof Object && y instanceof Object)) {
    return false;
  }

  if (x.isPrototypeOf(y) || y.isPrototypeOf(x)) {
    return false;
  }

  if (x.constructor !== y.constructor) {
    return false;
  }

  if (x.prototype !== y.prototype) {
    return false;
  }

  // Check for infinitive linking loops
  if (leftChain.indexOf(x) > -1 || rightChain.indexOf(y) > -1) {
    return false;
  }

  // Quick checking of one object being a subset of another.
  // todo: cache the structure of arguments[0] for performance
  for (p in y) {
    if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
      return false;
    } else if (typeof y[p] !== typeof x[p]) {
      return false;
    }
  }

  for (p in x) {
    if (y.hasOwnProperty(p) !== x.hasOwnProperty(p)) {
      return false;
    } else if (typeof y[p] !== typeof x[p]) {
      return false;
    }

    switch (typeof x[p]) {
      case "object":
      case "function":
        leftChain.push(x);
        rightChain.push(y);

        if (!areEqual(x[p], y[p])) {
          return false;
        }

        leftChain.pop();
        rightChain.pop();
        break;

      default:
        if (x[p] !== y[p]) {
          return false;
        }
        break;
    }
  }

  return true;
};

// ** Returns K format from a number
export const kFormatter = (num: number) =>
  num > 999 ? `${(num / 1000).toFixed(1)}k` : num;

// ** Converts HTML to string
export const htmlToString = (html: any) => html.replace(/<\/?[^>]+(>|$)/g, "");

// ** Checks if the passed date is today
export const isToday = (date: Date) => {
  const today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  );
};

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (
  value: string,
  formatting: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  }
) => {
  if (!value) return value;
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

export const formatDateWithTime = (
  value: string,
  formatting: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
  }
) => {
  if (!value) return value;
  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

// ** Returns short month of passed date
export const formatDateToMonthShort = (value: string) => {
  let formatting: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };

  return new Intl.DateTimeFormat("en-US", formatting).format(new Date(value));
};

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => !!sessionStorage.getItem("userData");

export const getUserData = () =>
  JSON.parse(sessionStorage.getItem("userData") as string);

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
// export const getHomeRouteForLoggedInUser = (userRole: any) => {
//   if (userRole === "admin") return "/";
//   if (userRole === "client") return "/access-control";
//   return "/login";
// };

export const delay = (ms: any) => new Promise((res) => setTimeout(res, ms));

export const getGraphData = (data: any[]) => {
  const newObject: any = {};
  data.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (!newObject[key]) {
        newObject[key] = [];
      }
      newObject[key].push(item[key]);
    });
  });
};

export const getGraphData2 = (data: any[], key: string, live = false) => {
  const newList = [];
  if (live) {
    for (let i = 0; i < data.length; i++) {
      newList.push({
        ...data[i],
        dayMonth: formatDateToMonthShort(data[i].createdAt),
      });
    }
    return [...newList.reverse()];
  }

  for (let i = 0; i < data.length; i++) {
    if (i === 0 || data[i][key] !== data[i - 1][key]) {
      newList.push({
        ...data[i],
        dayMonth: formatDateToMonthShort(data[i].createdAt),
      });
    }
  }
  const lengthDiff = 5 - newList.length;
  if (lengthDiff <= 0) {
    return [...newList.reverse()];
  } else {
    const resultArray = [...newList];
    for (let i = 0; i < lengthDiff; i++) {
      if (resultArray.length < 5) {
        resultArray.unshift(resultArray[0]);
      }
      if (resultArray.length < 5) {
        resultArray.push(resultArray[resultArray.length - 1]);
      }
    }
    return [...resultArray.reverse()];
  }
};

export const getRandomColor = () => {
  var letters = "0123456789ABCDEF";
  var color = "#";
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const toCapOrUpperCase = (str: string) => {
  if (str.length <= 3) {
    return str.toUpperCase();
  } else {
    return str.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
      return str.toUpperCase();
    });
  }
};
